// URL builder for the unified consolidated-effort endpoints.
//
// Every filter is optional and omitted when absent, so the same builder serves the Consolidated
// Report, the dashboard efforts-summary card, the leave summary and the export. Callers pass a
// filter object rather than a positional argument list, which is what stops a new filter from
// having to be threaded through every existing call site.
// @since NEXT_VERSION
import * as AppConstants from "AppConstants";
import { getDateInFormat } from "utils/dateUtil";

// Values the backend treats as "no filter". Sending them explicitly would narrow the scope to
// nothing rather than widening it, so they are dropped here instead of at each call site.
const isAbsent = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * Builds the query string shared by both unified endpoints.
 * @since NEXT_VERSION
 */
export const buildEffortsQuery = ({
  fromDate,
  toDate,
  managerIds,
  includeSubtree,
  clientIds,
  ownerIds,
  ownerType,
  effortTypes,
} = {}) => {
  const params = new URLSearchParams();

  // Comma-joined rather than repeated: matches Spring's List<Long> @RequestParam binding.
  const csv = (value) => (Array.isArray(value) ? value : [value]).join(",");

  params.append("fromDate", getDateInFormat(fromDate));
  params.append("toDate", getDateInFormat(toDate));

  if (!isAbsent(managerIds)) params.append("managerIds", csv(managerIds));
  // Only sent when explicitly false: the backend defaults it to true.
  if (includeSubtree === false) params.append("includeSubtree", "false");
  if (!isAbsent(clientIds)) params.append("clientIds", csv(clientIds));
  if (!isAbsent(ownerIds)) {
    params.append("ownerIds", csv(ownerIds));
    params.append("ownerType", ownerType ?? AppConstants.OWNER_TYPE_MEMBER);
  }
  if (!isAbsent(effortTypes)) params.append("effortTypes", csv(effortTypes));

  return params.toString();
};

/** Detail rows — one per activity, leave plan or holiday. @since NEXT_VERSION */
export const getUrlForUnifiedEfforts = (filter) =>
  `${AppConstants.GET_UNIFIED_EFFORTS_ENDPOINT}?${buildEffortsQuery(filter)}`;

/** Monthly roll-up — one row per owner per month. @since NEXT_VERSION */
export const getUrlForUnifiedEffortsMonthly = (filter) =>
  `${AppConstants.GET_UNIFIED_EFFORTS_MONTHLY_ENDPOINT}?${buildEffortsQuery(filter)}`;

/** Leave plans over the same scope, in the LeavePlan response shape. @since NEXT_VERSION */
export const getUrlForUnifiedLeave = (filter) =>
  `${AppConstants.GET_UNIFIED_LEAVE_ENDPOINT}?${buildEffortsQuery(filter)}`;
